import { useEffect, useState } from 'react';
import { Mic, MapPin, X, ArrowRight } from 'lucide-react';
import { stores as storesApi } from '../lib/api';
import VoiceOrderButton from './VoiceOrderButton';

/**
 * Home-screen "Order by Voice" launcher.
 *
 * A single tap gets elderly / low-literacy users to a shop, then to the mic.
 * We surface the customer's most recent stores first, then the top-rated
 * ones near them — no typing required.
 */
export default function VoiceOrderLauncher() {
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || rows.length) return;
    setLoading(true);
    storesApi.list().then((s) => setRows(s || [])).finally(() => setLoading(false));
  }, [open, rows.length]);

  return (
    <>
      <section
        data-testid="home-voice-order-card"
        className="max-w-3xl mx-auto px-4 md:px-6 pt-3"
      >
        <div className="rounded-xl border-2 border-[#0D3F12] bg-[#0D3F12] text-white p-4 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white/15 grid place-items-center shrink-0">
            <Mic className="w-7 h-7"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-black text-white/70">
              WhatsApp voice note
            </div>
            <div className="font-heading font-black text-lg mt-0.5">Voice note to shop</div>
            <div className="text-xs text-white/80 mt-0.5">
              No typing — just talk. We send your voice to the shop&apos;s WhatsApp.
            </div>
          </div>
          <button
            data-testid="home-voice-order-btn"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1 rounded-lg bg-white text-[#0D3F12] font-black uppercase text-xs tracking-wider h-11 px-4"
          >
            Start <ArrowRight className="w-4 h-4"/>
          </button>
        </div>
      </section>

      {open && (
        <div
          data-testid="voice-order-store-picker"
          className="fixed inset-0 z-[55] bg-black/50 grid place-items-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white w-full max-w-lg rounded-2xl p-5 max-h-[85vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <Mic className="w-4 h-4 text-[#0D3F12]"/>
              <div className="font-heading font-black text-xl">Which shop are you calling?</div>
              <button onClick={() => setOpen(false)} className="ml-auto text-ink/40 hover:text-ink" aria-label="Close">
                <X className="w-5 h-5"/>
              </button>
            </div>
            <p className="text-xs text-ink/60 mt-1">Pick your shop, then talk to them.</p>

            <ul className="mt-3 space-y-1 overflow-y-auto flex-1">
              {loading && <li className="text-ink/50 text-sm">Loading shops…</li>}
              {!loading && rows.length === 0 && <li className="text-ink/50 text-sm">No shops in your area yet.</li>}
              {rows.slice(0, 30).map((s) => (
                <li
                  key={s.id}
                  data-testid={`voice-picker-store-${s.id}`}
                  className={`rounded-lg border-2 p-3 flex items-center gap-3 cursor-pointer transition ${
                    chosen?.id === s.id ? 'border-[#0D3F12] bg-[#E8F5E9]' : 'border-ink/10 hover:border-ink'
                  }`}
                  onClick={() => setChosen(s)}
                >
                  <div className="w-10 h-10 rounded-md bg-muted grid place-items-center overflow-hidden shrink-0">
                    {s.image_url ? (
                      <img src={s.image_url} alt="" className="w-full h-full object-cover"/>
                    ) : (
                      <MapPin className="w-4 h-4 text-ink/40"/>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-sm text-ink truncate">{s.name}</div>
                    <div className="text-[11px] text-ink/60 truncate">
                      {s.category} · {s.village}
                    </div>
                  </div>
                  <span className="text-[10px] font-mono font-bold text-ink/50 uppercase">
                    {chosen?.id === s.id ? '✓ chosen' : ''}
                  </span>
                </li>
              ))}
            </ul>

            {chosen && (
              <div className="mt-3 pt-3 border-t border-ink/10">
                <VoiceOrderButton store={chosen} size="large"/>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
